import { ChevronLeft } from 'lucide-react';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button, H2, XStack, YStack } from 'tamagui';
import { tokens } from 'tamagui-config';

import { CustomDialog, GradientButton, PinEntryStep, UnlockKeypad } from '../../components/common';
import { usePinFlow, PinFlowStep } from '../../hooks';
import { ROUTE_PATHNAMES } from '../../routes/routeConstants';
import { StyledCard } from '../../screens/Settings_General/components/styles';

interface PinFlowScreenProps {
  type: 'set' | 'reset' | 'disable';
  titleKey: string;
  currentPin?: string[];
}

export const PinFlowScreen: React.FC<PinFlowScreenProps> = ({ type, titleKey, currentPin }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    step,
    pin,
    setPin,
    error,
    isSaveEnabled,
    showModal,
    setShowModal,
    handleSuccess,
    handleSave,
    handleConfirmSave,
    validPin,
  } = usePinFlow({
    type,
    currentPin,
    onComplete: () => navigate(ROUTE_PATHNAMES.lockScreen),
  });

  const goBack = () => navigate(ROUTE_PATHNAMES.lockScreen);

  const dialogProps =
    type === 'disable'
      ? {
          buttonLabel: t('settings.lockScreen.ok'),
          title: t('settings.lockScreen.pinDisabled'),
          description: t('settings.lockScreen.pinDisabledDescription'),
          onButtonPress: async () => {
            setShowModal(false);
            await handleConfirmSave();
          },
        }
      : {
          buttonLabel: t('settings.lockScreen.ok'),
          title: t('settings.lockScreen.pinSet'),
          description: t('settings.lockScreen.pinSetDescription'),
          onButtonPress: async () => {
            handleConfirmSave();
          },
        };

  return (
    <>
      <YStack padding="$5">
        <StyledCard height="432px">
          <YStack gap="$5">
            <XStack justifyContent="space-between" alignItems="center">
              <XStack alignItems="center" gap="$3.5">
                <Button
                  icon={<ChevronLeft size={24} strokeWidth={4} color={tokens.color.white12} />}
                  circular
                  minWidth={72}
                  minHeight={72}
                  borderWidth="$1"
                  borderColor="$transparentBorder"
                  onPress={goBack}
                  backgroundColor="$transparentBorder"
                />
                <H2 color="$white12" fontSize="$6" lineHeight="$5">
                  {t(titleKey)}
                </H2>
              </XStack>
              {(step === PinFlowStep.NewPin || step === PinFlowStep.ConfirmPin) && (
                <GradientButton
                  label={t('settings.lockScreen.save')}
                  isActive={!!isSaveEnabled}
                  width="118px"
                  onPress={handleSave}
                />
              )}
            </XStack>
            <XStack gap="$5">
              <XStack flex={1} minWidth={0} width="50%" alignItems="center">
                <PinEntryStep
                  type={type}
                  step={step}
                  pin={pin}
                  setPin={setPin}
                  onSuccess={() => handleSuccess()}
                  validPin={validPin}
                  error={error}
                />
              </XStack>
              <XStack flex={1} minWidth={0} width="50%">
                <UnlockKeypad
                  onNumberClick={(number) => {
                    const firstEmpty = pin.findIndex((d) => d === '');
                    if (firstEmpty !== -1) {
                      const updated = [...pin];
                      updated[firstEmpty] = number;
                      setPin(updated);
                    }
                  }}
                  onDelete={() => {
                    const lastFilled = [...pin].reverse().findIndex((d) => d !== '');
                    if (lastFilled !== -1) {
                      const index = 3 - lastFilled;
                      const updated = [...pin];
                      updated[index] = '';
                      setPin(updated);
                    }
                  }}
                />
              </XStack>
            </XStack>
          </YStack>
        </StyledCard>
      </YStack>
      <CustomDialog
        width="140px"
        open={showModal}
        onOpenChange={setShowModal}
        onButtonPress={dialogProps.onButtonPress}
        buttonLabel={dialogProps.buttonLabel}
        title={dialogProps.title}
        description={dialogProps.description}
      />
    </>
  );
};
